import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useDoublePointsCampaignTier from '../hooks/useDoublePointsCampaignTier'
import { useAppSelector } from 'hooks/reduxHooks'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'

const Card = styled(Pane)`
  padding: ${rem(24)};
  ${setSvgIconSize(20)}
  max-width: ${rem(360)};

  &.tier-bronze {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    background-color: ${props => props.theme.palette.product.loyalty.silver.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    background-color: ${props => props.theme.palette.product.loyalty.gold.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const IconBadge = styled.div`
  border-radius: ${props => props.theme.borderRadius.round};
  width: ${rem(32)};
  height: ${rem(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &.tier-bronze {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.darkContrast};
  }

  &.tier-silver {
    background-color: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
  }

  &.tier-gold {
    background-color: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
  }

  &.tier-platinum {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

interface Benefit {
  title: string
  icon: React.ReactNode
}

const LOUNGE_PASS_REGIONS = ['AU', 'SG', 'NZ']

function DoublePointTakeoverTopBenefits() {
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const [tier, tierName, , nextTierName] = useCurrentLoyaltyTier()
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const displayTier = useDoublePointsCampaignTier()

  const topBenefits = useMemo<Array<Benefit>>(() => {
    const isLoungePassRegion = LOUNGE_PASS_REGIONS.includes(regionCode)

    if (isLoungePassRegion) {
      // AU, SG, NZ regions
      switch (displayTier) {
        case 'bronze':
          // Non-member/logged out
          return [
            { title: 'Free room or package upgrade', icon: <LineBedIcon /> },
            { title: 'Free airport lounge passes', icon: <LineChairWithAirplaneIcon /> },
            { title: 'Free airport chauffeur service', icon: <LineTaxiIcon /> },
          ]
        case 'silver':
          // Bronze → Silver
          return [
            { title: '1x Free room or package upgrade', icon: <LineBedIcon /> },
            { title: '2x Airport lounge passes', icon: <LineChairWithAirplaneIcon /> },
            { title: 'Priority phone support 24/7', icon: <LinePhoneIcon /> },
          ]
        case 'gold':
          // Silver → Gold
          return [
            { title: '2x Free room or package upgrade', icon: <LineBedIcon /> },
            { title: '2x Airport lounge passes', icon: <LineChairWithAirplaneIcon /> },
            { title: '2x airport chauffeur service', icon: <LineTaxiIcon /> },
          ]
        case 'platinum':
          // Gold → Platinum
          return [
            { title: '4x Free room or package upgrade', icon: <LineBedIcon /> },
            { title: '2x Airport lounge passes', icon: <LineChairWithAirplaneIcon /> },
            { title: 'Unlimited airport chauffeur service', icon: <LineTaxiIcon /> },
          ]
      }
    } else {
      // All other regions
      switch (displayTier) {
        case 'bronze':
          // Non-member/logged out
          return [
            { title: 'Free room or package upgrades', icon: <LineBedIcon /> },
            { title: 'Free airport chauffeur service', icon: <LineTaxiIcon /> },
            { title: 'Travel protection discounts', icon: <LineShieldCheckIcon /> },
          ]
        case 'silver':
          // Bronze → Silver
          return [
            { title: '1x Free room or package upgrade', icon: <LineBedIcon /> },
            { title: 'Travel protection discounts', icon: <LineShieldCheckIcon /> },
            { title: 'Priority phone support 24/7', icon: <LinePhoneIcon /> },
          ]
        case 'gold':
          // Silver → Gold
          return [
            { title: '2x Free room or package upgrade', icon: <LineBedIcon /> },
            { title: '2x Free airport chauffeur service', icon: <LineTaxiIcon /> },
            { title: 'Priority phone support 24/7', icon: <LinePhoneIcon /> },
          ]
        case 'platinum':
          // Gold → Platinum
          return [
            { title: '4x Free room or package upgrade', icon: <LineBedIcon /> },
            { title: 'Unlimited airport chauffeur service', icon: <LineTaxiIcon /> },
            { title: '25% travel protection discount', icon: <LineShieldCheckIcon /> },
          ]
      }
    }
  }, [displayTier, regionCode])

  const HeadingText = useMemo(() => {
    if (hasLoyaltyAccount) {
      if (tier === 'platinum') {
        return `Top benefits of ${tierName}`
      }
      return `Top benefits of ${nextTierName ?? tierName}`
    }
    return `Top benefits of ${loyaltyProgramName}`
  }, [hasLoyaltyAccount, tier, tierName, nextTierName, loyaltyProgramName])

  // Link logic:
  // - Bronze (not a member): scroll to benefits table on current page
  // - Platinum: scroll to benefits table on current page
  // - Others (Silver/Gold): redirect to benefits tab with scroll to next tier benefits
  const viewAllUrl = useMemo(() => {
    if (displayTier === 'bronze' || tier === 'platinum') return '/double-status-credits#about-benefits'
    return '/societe#benefits-your-benefits'
  }, [displayTier, tier])

  return (
    <Card className={cn(`tier-${displayTier}`)} borderRadius="L" border="transparent">
      <VerticalSpacer gap={16}>
        <Group fullWidth direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <BodyText variant="large" weight="bold">{HeadingText}</BodyText>
          <TextLink size="small" to={viewAllUrl}>View all</TextLink>
        </Group>
        <VerticalSpacer gap={12}>
          {topBenefits.map(benefit => (
            <Group key={benefit.title} direction="horizontal" gap={12} verticalAlign="center">
              <IconBadge className={cn(`tier-${displayTier}`)}>
                {benefit.icon}
              </IconBadge>
              <BodyText variant="medium">{benefit.title}</BodyText>
            </Group>
          ))}
        </VerticalSpacer>
      </VerticalSpacer>
    </Card>
  )
}

export default DoublePointTakeoverTopBenefits
